import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useRequest } from 'shared/hooks';
import {
  AsyncMultipleSelect,
  AsyncSearchableSelect, DependentAsyncMultipleSelect,
  StaticSearchableSelect,
} from 'shared/components/select/SearchableSelects';
import { capitalize, compareArrays } from 'shared/utils';

/**
 * Higher-order function that creates an async searchable select editor
 * Returns a component that renders a SearchableSelect with the passed in props
 * Useful for configuring datatable filters and dataFormatters
 * @param {Object} searchableSelectProps - Configuration for the searchable select
 * @param {Function} [searchableSelectProps.getOptionValue] - Function to extract value from
 * option object
 * @param {Function} searchableSelectProps.getRequest - Required for syncing searchable select
 * value with parent value
 * @returns {Function} SearchableSelectEditor component
 */
export function withAsyncSelect(searchableSelectProps) {
  const {
    getOptionValue = (o) => o.id,
    getRequest, // Required for syncing searchable selects value and parents value.
    ...rest
  } = searchableSelectProps;

  /**
   * @param {Object} props
   * @param {string} props.label - Label for the select field
   * @param {string|number} [props.value=''] - Current selected value
   * @param {Function} props.setValue - Function to update the value
   */
  function SearchableSelectEditor({ label, value = '', setValue }) {
    const { request } = useRequest();
    const [selectedOption, setSelectedOption] = useState(null);

    useEffect(() => {
      (async () => {
        if (!value) {
          if (selectedOption) {
            setSelectedOption(null);
          }
          return;
        }
        const selectedValue = selectedOption && getOptionValue(selectedOption);
        const matches = selectedValue === value || selectedValue === parseInt(value, 10);
        if (!selectedOption || !matches) {
          const result = await request(getRequest(value));
          if (result) {
            setSelectedOption(result);
          }
        }
      })();
    }, [selectedOption, request, value]);

    const onChange = useCallback((option) => {
      if (!option) {
        setSelectedOption(null);
        setValue(0);
      } else {
        setSelectedOption(option);
        setValue(getOptionValue(option));
      }
    }, [setValue]);

    return (
      <AsyncSearchableSelect
        getOptionValue={getOptionValue}
        label={label}
        onChange={onChange}
        selectedOption={selectedOption}
        {...rest}
      />
    );
  }

  return SearchableSelectEditor;
}

/**
 * Higher-order function that creates a static searchable select editor
 * @param {Object} searchableSelectProps - Configuration for the searchable select
 * @param {boolean} [searchableSelectProps.valueAsLabel=false] - If passing options in the form
 * ['option1', 'option2'], set to true
 * @param {Function} [searchableSelectProps.getOptionValue] - Function to extract value from
 * option object
 * @param {Function} [searchableSelectProps.getOptionLabel] - Function to get label from option
 * object
 * @param {Array} searchableSelectProps.options - Required for syncing searchable select value
 * with parent value
 * @returns {Function} StaticSearchableSelectEditor component
 */
export function withStaticSelect(searchableSelectProps) {
  const {
    valueAsLabel = false, // if passing options in the form ['options1', 'options2'], set to true
    getOptionValue = (o) => o.value,
    getOptionLabel = (o) => capitalize(o.label),
    options, // Required for syncing searchable selects value and parents value.
    ...rest
  } = searchableSelectProps;

  /**
   * @param {Object} props
   * @param {string} props.label - Label for the select field
   * @param {string|number|Object} [props.value=null] - Current selected value
   * @param {Function} props.setValue - Function to update the value
   */
  function StaticSearchableSelectEditor({
    label, value = null, setValue,
  }) {
    const [selectedOption, setSelectedOption] = useState(null);
    const optionArray = useMemo(
      () => (valueAsLabel ? options.map((r) => ({ label: r, value: r })) : options),
      [],
    );

    useEffect(() => {
      const matchedOption = optionArray.find((item) => getOptionValue(item) === value);
      if (matchedOption) {
        setSelectedOption(matchedOption);
      } else {
        setSelectedOption(null);
      }
    }, [optionArray, selectedOption, value]);

    const onChange = useCallback((option) => {
      if (!option) {
        setSelectedOption(null);
        setValue(null);
      } else {
        setSelectedOption(option);
        setValue(getOptionValue(option));
      }
    }, [setValue]);

    return (
      <StaticSearchableSelect
        label={label}
        onChange={onChange}
        staticOptions={optionArray}
        selectedOption={selectedOption}
        getOptionLabel={getOptionLabel}
        {...rest}
      />
    );
  }

  return StaticSearchableSelectEditor;
}

/**
 * Higher-order function that creates a multiple select editor with async options
 * @param {Object} searchableSelectProps - Configuration for the searchable select
 * @param {Function} [searchableSelectProps.getOptionValue] - Function to extract value from
 * option object
 * @param {Function} searchableSelectProps.getRequest - Required for syncing searchable select
 * value with parent value
 * @returns {Function} MultipleSelectEditor component
 */
export function withMultipleSelect(searchableSelectProps) {
  const {
    getOptionValue = (o) => o.id,
    getRequest, // Required for syncing searchable selects value and parents value.
    ...rest
  } = searchableSelectProps;

  /**
   * @param {Object} props
   * @param {string} props.label - Label for the select field
   * @param {*} [props.value=[]] - Current selected values (array or single value)
   * @param {Function} props.setValue - Function to update the value
   * @param {Object} props.dirtyFilterState - Filter state object for base search
   * @param {string} [props.baseSearchParam=''] - Base search parameter key
   */
  function MultipleSelectEditor({
    label, value = [], setValue, dirtyFilterState, baseSearchParam = '',
  }) {
    const { request } = useRequest();
    const [selectedOptions, setSelectedOptions] = useState([]);

    useEffect(() => {
      (async () => {
        if (!value || value?.length === 0) {
          if (selectedOptions.length !== 0) {
            setSelectedOptions([]);
          }
          return;
        }
        // always sets value to an array of integers for comparison and requests
        const newValue = Array.isArray(value) ? value.map((val) => parseInt(val, 10))
          : [parseInt(value, 10)];
        const selectedValues = selectedOptions
          && selectedOptions.map((so) => getOptionValue(so));
        const matches = compareArrays(selectedValues, newValue);
        if (selectedOptions.length === 0 || !matches) {
          const result = await Promise
            .all(newValue.map(
              (val) => request(getRequest(val)),
            ));
          if (result) {
            setSelectedOptions(result);
          }
        }
      })();
    }, [selectedOptions, request, value]);

    const onChange = useCallback((options) => {
      if (options.length === 0) {
        setSelectedOptions([]);
        setValue([]);
      } else {
        setSelectedOptions(options);
        setValue(options.map((option) => getOptionValue(option)));
      }
    }, [setValue]);

    return (
      <AsyncMultipleSelect
        getOptionValue={getOptionValue}
        label={label}
        onChange={onChange}
        selectedOptions={selectedOptions}
        baseSearch={baseSearchParam ? `${baseSearchParam}=${dirtyFilterState[baseSearchParam]}` : ''}
        {...rest}
      />
    );
  }

  return MultipleSelectEditor;
}

export function withDependentMultipleSelect(searchableSelectProps) {
  const {
    getOptionValue = (option) => option?.value,
    getRequest,
    dependentKey = '',
    ...rest
  } = searchableSelectProps;

  function DependentMultipleSelectEditor({
    label, value = [], setValue, dirtyFilterState, baseSearchParam = '',
  }) {
    const { request } = useRequest();
    const [selectedOptions, setSelectedOptions] = useState([]);

    useEffect(() => {
      (async () => {
        if (!value || value?.length === 0) {
          if (selectedOptions.length !== 0) {
            setSelectedOptions([]);
          }
          return;
        }
        // always sets value to an array of integers for comparison and requests
        const newValue = Array.isArray(value) ? value.map((val) => parseInt(val, 10))
          : [parseInt(value, 10)];
        const selectedValues = selectedOptions
          && selectedOptions.map((so) => getOptionValue(so));
        const matches = compareArrays(selectedValues, newValue);
        if (selectedOptions.length === 0 || !matches) {
          const result = await Promise
            .all(newValue.map(
              (val) => request(getRequest(val)),
            ));
          if (result) {
            setSelectedOptions(result);
          }
        }
      })();
    }, [selectedOptions, request, value]);

    const onChange = useCallback((options) => {
      if (options.length === 0) {
        setSelectedOptions([]);
        setValue([]);
      } else {
        setSelectedOptions(options);
        setValue(options.map((option) => getOptionValue(option)));
      }
    }, [setValue]);

    const dependentId = useMemo(
      () => (dependentKey ? dirtyFilterState[dependentKey] : ''),
      [dirtyFilterState],
    );

    useEffect(() => {
      const parentIds = new Set(selectedOptions.map((so) => so[dependentKey]));
      if (selectedOptions.length > 0 && (parentIds.size > 1 || !parentIds.has(+dependentId))) {
        setSelectedOptions([]);
        setValue([]);
      }
    }, [dependentId, selectedOptions, setValue]);

    return (
      <DependentAsyncMultipleSelect
        getOptionValue={getOptionValue}
        label={label}
        onChange={onChange}
        selectedOptions={selectedOptions}
        baseSearch={baseSearchParam ? `${baseSearchParam}=${dirtyFilterState[baseSearchParam]}` : ''}
        dependentId={dependentId}
        {...rest}
      />
    );
  }

  return DependentMultipleSelectEditor;
}
